// React
import { useCallback } from 'react';
import { useLoaderData } from 'react-router-dom';

// Hooks
import { usePreviewData } from '@/hooks/usePreviewData';

// Utils
import { getSlug } from '@/utils/slug';

// Services
import { getHeader } from '@/services/header/headerService';
import { getFooter } from '@/services/footer/footerService';
import { getOffer } from '@/services/offer/offerService';

// Components
import LayoutHeaderFooter from '@/components/layout/LayoutHeaderFooter/LayoutHeaderFooter';
import { PageBlocks } from '@/components/blocks/Blocks';
import { Helmet } from 'react-helmet';
import { RoutesEnum } from '@/enum/RoutesEnum';

export async function offerLoader({ request }: { request: Request }) {
  const slug = getSlug(new URL(request.url));

  if (!slug) return null;

  const [header, footer, offer] = await Promise.all([getHeader(), getFooter(), getOffer(slug)]);

  return {
    header,
    footer,
    offer,
  };
}

function Offer() {
  const loaderData = useLoaderData() as Awaited<ReturnType<typeof offerLoader>>;
  const refetch = useCallback(
    () => offerLoader({ request: new Request(window.location.href) }),
    [],
  );
  const data = usePreviewData(loaderData, refetch);
  const baseUrl = import.meta.env.VITE_DOMAIN;

  if (!data) return null;

  const { header, footer, offer } = data;

  if (!offer) return null;

  return (
    <main>
      <Helmet>
        <title>{offer.seo.metaTitle}</title>
        <meta name="description" content={offer.seo.metaDescription} />
        <link rel="canonical" href={`${baseUrl}/${RoutesEnum.OFFERS}/${offer.slug}`} />
      </Helmet>
      <LayoutHeaderFooter header={header} footer={footer}>
        <div>
          <PageBlocks blocks={offer.blocks} />
        </div>
      </LayoutHeaderFooter>
    </main>
  );
}

export default Offer;
